// src/utils/artAssetDossierPdf.js
// Art Asset Dossier (§6) — dossier patrimonial / financement d'UNE œuvre.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import i18next from "i18next";
import { computeArtworkReadiness, READINESS_DIMENSIONS } from "./readiness";
import { BRAND_UPPER } from "../lib/rebrand";
import { valuationFooter } from "./valuationDisclosure";

const tr = (key, fallback, options = {}) =>
  i18next.t(key, { defaultValue: fallback, ...options });

const currentLocale = () => {
  const lang = String(i18next.resolvedLanguage || i18next.language || "fr").split("-")[0];
  return lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "fr-FR";
};

// Section « Œuvre numérique et accès » : textes locaux en français, anglais et allemand.
const DIGITAL_T = {
  fr: { title: "Œuvre numérique et accès associés", source: "Plateforme source", sourceId: "Identifiant chez la plateforme", sourceUrl: "Page de l'œuvre sur la plateforme", sourceCollection: "Collection thématique", importedOn: "Importée le", format: "Média et format", usage: "Usage et affichage", equipment: "Équipement d'affichage", resale: "Conditions de revente", transmission: "Conditions connues de transmission", custodian: "Qui détient l'accès", procedure: "Procédure à suivre", heirs: "Instruction laissée aux héritiers", note: "Aucun mot de passe ni clé n'est conservé : seules figurent la localisation de l'accès, son dépositaire et la procédure.", notSet: "à renseigner" },
  en: { title: "Digital artwork and related access", source: "Source platform", sourceId: "Platform identifier", sourceUrl: "Artwork page on the platform", sourceCollection: "Thematic collection", importedOn: "Imported on", format: "Medium and format", usage: "Usage and display", equipment: "Display equipment", resale: "Resale conditions", transmission: "Known transfer conditions", custodian: "Who holds the access", procedure: "Procedure to follow", heirs: "Instruction left for the heirs", note: "No password or key is kept: only where the access is held, who holds it and the procedure are recorded.", notSet: "to be completed" },
  de: { title: "Digitales Kunstwerk und zugehöriger Zugang", source: "Quellplattform", sourceId: "Kennung bei der Plattform", sourceUrl: "Seite des Werks auf der Plattform", sourceCollection: "Thematische Sammlung", importedOn: "Importiert am", format: "Medium und Format", usage: "Nutzung und Anzeige", equipment: "Anzeigegerät", resale: "Bedingungen für den Weiterverkauf", transmission: "Bekannte Übertragungsbedingungen", custodian: "Wer den Zugang verwahrt", procedure: "Einzuhaltendes Verfahren", heirs: "Anweisung an die Erben", note: "Es werden weder Passwörter noch Schlüssel gespeichert: Festgehalten werden nur der Ort des Zugangs, wer ihn verwahrt und das Verfahren.", notSet: "noch auszufüllen" },
};
const digitalT = () => DIGITAL_T[String(currentLocale()).slice(0, 2)] || DIGITAL_T.fr;
const isDigitalArtwork = (a) => a?.medium_category === "nft" || (a?.digital_asset && typeof a.digital_asset === "object") || !!a?.external_source;

const fmt = (d) => (d ? new Date(d).toLocaleDateString(currentLocale()) : "—");
const yn = (v) => (v ? tr("artAssetDossierPdf.common.yes", "Oui") : tr("artAssetDossierPdf.common.no", "Non"));
const money = (v, cur = "EUR") =>
  v || v === 0 ? `${Number(v).toLocaleString(currentLocale())} ${cur || "EUR"}` : "—";
const val = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

function mediumLabel(a) {
  if (a?.medium_display_override) return a.medium_display_override;

  const key = String(a?.medium_normalized_key || a?.medium_key || a?.technique_key || "").trim();
  if (key) {
    const candidates = [
      `artwork.techniques.${key}`,
      `artwork.mediums.${key}`,
      `mediums.techniques.${key}`,
      `mediums.${key}`,
      `common.mediums.${key}`,
    ];
    const found = candidates.find((translationKey) => i18next.exists(translationKey));
    if (found) return i18next.t(found);
  }

  return a?.medium || a?.medium_original || a?.medium_normalized_key || "—";
}
function dimensionsLabel(a) {
  if (a?.dimensions) return a.dimensions;
  const h = a?.dimensions_height ?? a?.height, w = a?.dimensions_width ?? a?.width, d = a?.dimensions_depth ?? a?.depth;
  const u = a?.dimensions_unit || a?.unit || "cm";
  const parts = [h, w, d].filter((x) => x || x === 0);
  return parts.length ? `${parts.join(" × ")} ${u}` : "—";
}
function comparablesCount(a) {
  const c = a?.cup_comparables;
  if (Array.isArray(c)) return c.length;
  if (c && Array.isArray(c.items)) return c.items.length;
  return 0;
}
function currentValue(a) {
  return a?.current_estimated_value ?? a?.valuation_estimated_value ?? a?.estimated_value ?? a?.value ?? null;
}

const ownershipLabel = (value) => {
  const fallbacks = { full: "Pleine propriété", usufruct: "Usufruit", bare_ownership: "Nue-propriété" };
  return fallbacks[value]
    ? tr(`artAssetDossierPdf.ownership.${value}`, fallbacks[value])
    : val(value);
};

const conditionLabel = (value) => {
  const fallbacks = {
    excellent: "Excellent",
    very_good: "Très bon",
    good: "Bon",
    fair: "Moyen",
    very_poor: "Très moyen",
    poor: "Mauvais",
    heavily_damaged: "Très abîmé (restaurable)",
    partially_restorable: "Partiellement restaurable",
    irreparable: "Irréparable",
  };
  return fallbacks[value]
    ? tr(`artAssetDossierPdf.condition.${value}`, fallbacks[value])
    : val(value);
};

export function generateArtAssetDossier(artwork, opts = {}) {
  const a = artwork || {};
  const { ownerName = "" } = opts;
  const doc = new jsPDF("p", "mm", "a4");
  const M = 16;
  let y = 18;

  const { score, dimensions } = computeArtworkReadiness(a);
  const missing = READINESS_DIMENSIONS.filter((d) => !dimensions[d.key]).map((d) => d.label);

  // ---- En-tête ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(150);
  doc.text(tr("artAssetDossierPdf.header.eyebrow", "DOSSIER PATRIMONIAL — ART ASSET DOSSIER"), M, y);
  doc.setFontSize(19); doc.setTextColor(20);
  y += 9; doc.text(doc.splitTextToSize(val(a.title) === "—" ? tr("artAssetDossierPdf.header.untitled", "Œuvre sans titre") : a.title, 150), M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(90);
  y += 7; doc.text(val(a.artist), M, y);

  doc.setFont("helvetica", "bold"); doc.setFontSize(22);
  doc.setTextColor(score >= 80 ? "#1a7a44" : score >= 50 ? "#9a6b1f" : "#b4443a");
  doc.text(`${score}%`, 210 - M, 22, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(150);
  doc.text(tr("artAssetDossierPdf.header.completeness", "Complétude du dossier"), 210 - M, 27, { align: "right" });

  y += 6;

  const section = (title, body) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
    if (y > 262) { doc.addPage(); y = 18; }
    doc.text(title, M, y);
    autoTable(doc, {
      startY: y + 2, theme: "plain", margin: { left: M, right: M },
      styles: { fontSize: 9.5, cellPadding: 1.4, textColor: 40 },
      columnStyles: { 0: { fontStyle: "bold", textColor: 110, cellWidth: 55 } },
      body,
    });
    y = (doc.lastAutoTable?.finalY || y) + 7;
  };

  section(tr("artAssetDossierPdf.sections.identification.title", "Identification"), [
    [tr("artAssetDossierPdf.sections.identification.artist", "Artiste"), val(a.artist)],
    [tr("artAssetDossierPdf.sections.identification.artworkTitle", "Titre"), val(a.title)],
    [tr("artAssetDossierPdf.sections.identification.year", "Année"), val(a.year)],
    [tr("artAssetDossierPdf.sections.identification.medium", "Médium"), mediumLabel(a)],
    [tr("artAssetDossierPdf.sections.identification.dimensions", "Dimensions"), dimensionsLabel(a)],
    [tr("artAssetDossierPdf.sections.identification.inventory", "N° d'inventaire"), val(a.inventory_number)],
  ]);

  section(tr("artAssetDossierPdf.sections.acquisition.title", "Propriété & acquisition"), [
    [tr("artAssetDossierPdf.sections.acquisition.owner", "Propriétaire"), ownerName || "—"],
    [tr("artAssetDossierPdf.sections.acquisition.ownershipStatus", "Statut de détention"), ownershipLabel(a.ownership_status)],
    [tr("artAssetDossierPdf.sections.acquisition.date", "Date d'acquisition"), fmt(a.acquisition_date)],
    [tr("artAssetDossierPdf.sections.acquisition.source", "Source"), val(a.acquisition_source || a.auction_house)],
    [tr("artAssetDossierPdf.sections.acquisition.price", "Prix d'acquisition"), money(a.acquisition_price_eur ?? a.acquisition_price, a.acquisition_currency || a.currency)],
    [
      tr("artAssetDossierPdf.sections.acquisition.documents", "Justificatifs"),
      (a.invoice === true || (a.documents && String(a.documents) !== "" && String(a.documents) !== "[]"))
        ? tr("artAssetDossierPdf.common.present", "Présents")
        : tr("artAssetDossierPdf.common.missing", "Manquants"),
    ],
  ]);

  section(tr("artAssetDossierPdf.sections.authenticity.title", "Authenticité & documentation"), [
    [tr("artAssetDossierPdf.sections.authenticity.invoice", "Facture"), yn(a.invoice === true)],
    [tr("artAssetDossierPdf.sections.authenticity.certificate", "Certificat d'authenticité"), yn(a.certificate_auth === true || (a.certificate_authenticity && a.certificate_authenticity !== ""))],
    [tr("artAssetDossierPdf.sections.authenticity.provenance", "Provenance"), val(a.provenance)],
    [tr("artAssetDossierPdf.sections.authenticity.signature", "Signature"), yn(a.signature === true || (a.signature_desc && a.signature_desc !== ""))],
  ]);

  section(tr("artAssetDossierPdf.sections.valuation.title", "Valorisation"), [
    [tr("artAssetDossierPdf.sections.valuation.currentValue", "Valeur actuelle"), money(currentValue(a), a.currency)],
    [tr("artAssetDossierPdf.sections.valuation.range", "Estimation basse / haute"), `${money(a.valuation_low ?? a.price_low, a.currency)}  →  ${money(a.valuation_high ?? a.price_high, a.currency)}`],
    [tr("artAssetDossierPdf.sections.valuation.method", "Méthode"), val(a.valuation_method || a.expert_method)],
    [tr("artAssetDossierPdf.sections.valuation.comparables", "Comparables"), String(comparablesCount(a))],
    [tr("artAssetDossierPdf.sections.valuation.date", "Date de valorisation"), fmt(a.last_valuation_date || a.expert_date || a.last_estimation_date)],
  ]);

  section(tr("artAssetDossierPdf.sections.market.title", "Marché (indicatif)"), [
    [tr("artAssetDossierPdf.sections.market.liquidity", "Liquidité indicative"), a.market_liquidity != null ? `${a.market_liquidity}/100` : "—"],
    [tr("artAssetDossierPdf.sections.market.trend", "Tendance"), a.market_trend != null ? `${a.market_trend}` : "—"],
    [tr("artAssetDossierPdf.sections.market.demand", "Demande"), val(a.market_demand)],
  ]);

  if (isDigitalArtwork(a)) {
    const D = digitalT();
    const d = a.digital_asset && typeof a.digital_asset === "object" ? a.digital_asset : {};
    const t = (v) => (v == null || String(v).trim() === "" ? D.notSet : String(v).trim());
    section(D.title, [
      [D.source, t(a.external_source)],
      [D.sourceId, t(a.external_id)],
      ...(a.source_url ? [[D.sourceUrl, a.source_url]] : []),
      ...(a.source_collection ? [[D.sourceCollection, a.source_collection]] : []),
      ...(a.imported_at ? [[D.importedOn, fmt(a.imported_at)]] : []),
      [D.format, t(d.format)],
      [D.usage, t(d.usage_rights)],
      [D.equipment, t(d.display_equipment)],
      [D.resale, t(d.resale_conditions)],
      [D.transmission, t(d.transmission_conditions)],
      [D.custodian, t(d.access_custodian)],
      [D.procedure, t(d.access_procedure)],
      ...(d.heir_instructions ? [[D.heirs, d.heir_instructions]] : []),
    ]);
    doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
    doc.text(doc.splitTextToSize(D.note, 180), M, y - 4);
    y += 4;
  }

  section(tr("artAssetDossierPdf.sections.risk.title", "Risque physique"), [
    [tr("artAssetDossierPdf.sections.risk.condition", "État de conservation"), conditionLabel(a.condition)],
    [tr("artAssetDossierPdf.sections.risk.lastInspection", "Dernier constat"), fmt(a.visited_at)],
    [tr("artAssetDossierPdf.sections.risk.storage", "Stockage"), val(a.storage_location_detail)],
    [
      tr("artAssetDossierPdf.sections.risk.insurance", "Assurance"),
      a.insurance_status === "insured"
        ? tr("artAssetDossierPdf.sections.risk.insured", "Assurée")
        : tr("artAssetDossierPdf.sections.risk.uninsured", "Non assurée"),
    ],
  ]);

  if (missing.length) {
    if (y > 250) { doc.addPage(); y = 18; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor("#b4443a");
    doc.text(tr("artAssetDossierPdf.missing.title", "Éléments à compléter pour un dossier complet"), M, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(90);
    y += 5;
    doc.text(doc.splitTextToSize("• " + missing.join("\n• "), 180), M, y);
    y += missing.length * 4.5 + 4;
  }

  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  const foot = tr(
    "artAssetDossierPdf.footer.disclaimer",
    "Dossier généré via " + BRAND_UPPER + " le {{date}}. " + BRAND_UPPER + " fournit une donnée structurée, traçable et partageable ; la décision (financement, cession, expertise) reste humaine et professionnelle. Les indicateurs de marché sont fournis à titre indicatif, avec avertissements méthodologiques.",
    { date: new Date().toLocaleDateString(currentLocale()) }
  );
  const valuationNote = valuationFooter([a], String(currentLocale()).slice(0, 2));
  doc.text(doc.splitTextToSize(valuationNote + " " + foot, 180), M, 281);

  const slug = (a.title || tr("artAssetDossierPdf.file.untitledSlug", "oeuvre")).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  const prefix = tr("artAssetDossierPdf.file.prefix", "dossier-patrimonial");
  doc.save(`${prefix}-${slug || tr("artAssetDossierPdf.file.untitledSlug", "oeuvre")}.pdf`);
}
